import { useEffect, useRef, useState } from "react";
import { resendAdminLoginOtp, verifyAdminLoginOtp } from "../../api/adminAuth";
import { API_ERROR_TYPE } from "../../constants/errorType";

const OTP_LENGTH = 6;
const CODE_EXPIRED_MESSAGE = "인증번호가 만료되었습니다. 재발송해 주세요.";

// challenge(10분)가 더 이상 유효하지 않아 1단계(이메일·비밀번호)로 돌아가야 하는 오류
const RESTART_ERROR_TYPES = [
  API_ERROR_TYPE.ADMIN_OTP_LOCKED,
  API_ERROR_TYPE.ADMIN_OTP_EXPIRED,
  API_ERROR_TYPE.ADMIN_OTP_RESEND_LIMIT,
];

// 남은 초를 mm:ss 형식으로 변환
const formatTime = (seconds) => {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

// 마감 시각(ms)까지 남은 초 (지났으면 0)
const secondsUntil = (deadline, now) => Math.max(0, Math.ceil((deadline - now) / 1000));

/**
 * 어드민 로그인 2단계 - SMS 인증번호 입력
 * @param {{ challengeId, maskedPhone, expiresIn, resendAvailableIn, smsSent }} challenge 1단계 응답
 * @param {(userInfo) => void} onVerified 인증 성공 시 호출 (기존 로그인 성공 처리)
 * @param {(message?: string) => void} onRestart 1단계로 돌아갈 때 호출 (message는 1단계 폼에 표시)
 */
function OtpVerificationStep({ challenge, onVerified, onRestart }) {
  const { challengeId, maskedPhone } = challenge;

  const [code, setCode] = useState("");
  const [smsSent, setSmsSent] = useState(challenge.smsSent !== false);
  // 타이머는 마감 시각 기준으로 계산 (백그라운드 탭에서 interval이 늦어져도 정확하도록)
  const [now, setNow] = useState(() => Date.now());
  const [expireAt, setExpireAt] = useState(() => Date.now() + (challenge.expiresIn ?? 0) * 1000);
  const [resendAt, setResendAt] = useState(() => Date.now() + (challenge.resendAvailableIn ?? 0) * 1000);
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const verifyingRef = useRef(false); // 중복 제출 방지 (state 반영 전 연속 호출 대비)
  const inputRef = useRef(null);

  const remainingSeconds = secondsUntil(expireAt, now);
  const resendRemainingSeconds = secondsUntil(resendAt, now);
  const isExpired = remainingSeconds === 0;

  // 1초마다 현재 시각 갱신 - 두 타이머가 모두 끝나면 멈추고, 언마운트(단계 전환) 시 정리
  useEffect(() => {
    if (Date.now() >= expireAt && Date.now() >= resendAt) return undefined;

    const timerId = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= expireAt && current >= resendAt) {
        clearInterval(timerId);
      }
    }, 1000);

    return () => clearInterval(timerId);
  }, [expireAt, resendAt]);

  // 처음 진입 시 인증번호 입력창에 포커스
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // 타이머 재설정 (재발송 성공, 쿨다운 응답)
  const resetTimers = ({ expiresIn, resendAvailableIn }) => {
    const current = Date.now();
    setNow(current);
    if (typeof expiresIn === "number") {
      setExpireAt(current + expiresIn * 1000);
    }
    if (typeof resendAvailableIn === "number") {
      setResendAt(current + resendAvailableIn * 1000);
    }
  };

  // OTP 단계 오류 처리 - alert 대신 입력창 아래 인라인으로 표시
  const handleOtpError = (error, fallbackMessage) => {
    const data = error?.response?.data;
    const type = data?.type;

    if (RESTART_ERROR_TYPES.includes(type)) {
      onRestart(data.detail || fallbackMessage);
      return;
    }

    if (type === API_ERROR_TYPE.ADMIN_OTP_INVALID) {
      setCode("");
      setErrorMessage(
        typeof data.remainingAttempts === "number"
          ? `인증번호가 올바르지 않습니다. (남은 시도 ${data.remainingAttempts}회)`
          : data.detail || "인증번호가 올바르지 않습니다.",
      );
      inputRef.current?.focus();
      return;
    }

    // 코드(3분)만 만료되고 challenge는 유효 - 2단계에 머물며 재발송 유도
    if (type === API_ERROR_TYPE.ADMIN_OTP_CODE_EXPIRED) {
      setCode("");
      resetTimers({ expiresIn: 0 });
      setErrorMessage(data.detail || CODE_EXPIRED_MESSAGE);
      return;
    }

    if (type === API_ERROR_TYPE.ADMIN_OTP_RESEND_COOLDOWN) {
      resetTimers({ resendAvailableIn: data.resendAvailableIn });
      setErrorMessage(data.detail || "잠시 후 다시 시도해 주세요.");
      return;
    }

    // 그 외 (재발송 중 otp-send-limit 포함) - 2단계에 머물며 인라인 표시
    setErrorMessage(data?.detail || fallbackMessage);
  };

  const submitCode = async (value) => {
    if (verifyingRef.current || isExpired || value.length !== OTP_LENGTH) return;

    verifyingRef.current = true;
    setIsVerifying(true);
    setErrorMessage("");

    try {
      const userInfo = await verifyAdminLoginOtp({ challengeId, code: value });
      // 성공 시 페이지 이동으로 언마운트되므로 verifyingRef는 풀지 않음 (이동 전 재제출 방지)
      onVerified(userInfo);
    } catch (error) {
      verifyingRef.current = false;
      setIsVerifying(false);
      handleOtpError(error, "인증 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
    }
  };

  const handleCodeChange = (event) => {
    // 숫자만 허용, 최대 6자리
    const digits = event.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    setCode(digits);

    // 6자리가 모두 입력되면 자동 제출
    if (digits.length === OTP_LENGTH) {
      submitCode(digits);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitCode(code);
  };

  const handleResend = async () => {
    if (isResending || resendRemainingSeconds > 0) return;

    setIsResending(true);
    setErrorMessage("");

    try {
      const data = await resendAdminLoginOtp({ challengeId });
      resetTimers({ expiresIn: data.expiresIn, resendAvailableIn: data.resendAvailableIn ?? 0 });
      setSmsSent(data.smsSent !== false);
      setCode("");
      inputRef.current?.focus();
    } catch (error) {
      handleOtpError(error, "인증번호 재발송 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="login-box">
      <h2>인증번호 입력</h2>
      <form onSubmit={handleSubmit}>
        <p className={smsSent ? "otp-guide" : "otp-guide otp-guide-error"}>
          {smsSent
            ? `${maskedPhone} 로 인증번호를 발송했습니다`
            : "문자 발송에 실패했습니다. 재발송을 눌러 주세요."}
        </p>
        <div className="input-group">
          <div className="otp-label-row">
            <label htmlFor="otpCode">인증번호</label>
            <span className="otp-timer" data-testid="otp-timer">
              {formatTime(remainingSeconds)}
            </span>
          </div>
          <div className="input-field-wrapper">
            <input
              ref={inputRef}
              type="text"
              id="otpCode"
              value={code}
              onChange={handleCodeChange}
              placeholder="인증번호 6자리"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_LENGTH}
              readOnly={isVerifying}
            />
          </div>
          {/* otp-code-expired 응답으로 같은 문구가 이미 표시 중이면 중복 표시하지 않음 */}
          {isExpired && errorMessage !== CODE_EXPIRED_MESSAGE && (
            <p className="login-inline-error" role="alert">
              {CODE_EXPIRED_MESSAGE}
            </p>
          )}
          {errorMessage && (
            <p className="login-inline-error" role="alert">
              {errorMessage}
            </p>
          )}
        </div>
        <button type="submit" disabled={isExpired || isVerifying || code.length !== OTP_LENGTH}>
          {isVerifying ? "확인 중..." : "확인"}
        </button>
        <div className="otp-secondary-actions">
          <button
            type="button"
            className="otp-secondary-btn"
            onClick={handleResend}
            disabled={isResending || resendRemainingSeconds > 0}
          >
            {resendRemainingSeconds > 0 ? `재발송 (${resendRemainingSeconds}초 후 가능)` : "인증번호 재발송"}
          </button>
          <button type="button" className="otp-secondary-btn" onClick={() => onRestart()}>
            처음으로
          </button>
        </div>
      </form>
    </div>
  );
}

export default OtpVerificationStep;
